import React, { useEffect, useRef, useState } from 'react'
import { Animated, StyleSheet } from 'react-native'
import type { StyleProp, ViewStyle } from 'react-native'
import equals from 'lodash/isEqual'
import union from 'lodash/union'
import styled from 'styled-components/native'

import { useOnLayout } from 'APP/Hooks/Layout'

type ChildKey = string | number

interface ChildrenState {
  dict: Record<ChildKey, React.ReactElement>
  order: ChildKey[]
}

interface IntakeTransitionProps {
  children: React.ReactElement | React.ReactElement[]
  animatedKey: ChildKey
  style?: StyleProp<ViewStyle>
}

const Container = styled.View``
const AnimatedContainer = styled(Animated.View)``

const getChildrenStateFromProps = (
  children: React.ReactElement | React.ReactElement[]
): ChildrenState => {
  const dict: Record<ChildKey, React.ReactElement> = {}
  const order: ChildKey[] = []

  React.Children.forEach(children, (child) => {
    const childElement = child as React.ReactElement
    const key = childElement.key as ChildKey
    dict[key] = childElement
    order.push(key)
  })

  return {
    dict,
    order,
  }
}

const IntakeTransition: React.FC<IntakeTransitionProps> = ({
  children: childrenProp,
  animatedKey,
  style,
}) => {
  const [childrenState, setChildrenState] = useState(getChildrenStateFromProps(childrenProp))
  const nextChildrenOrder = getChildrenStateFromProps(childrenProp).order
  const shouldAnimate = !equals(childrenState.order, nextChildrenOrder)

  const {
    layout: { height: containerHeight },
    onLayout: onContainerLayout,
  } = useOnLayout()
  const animatedState = useRef(new Animated.Value(1)).current

  useEffect(() => {
    setChildrenState(({ order, dict }) => {
      const { order: nextDesiredOrder, dict: nextDict } = getChildrenStateFromProps(childrenProp)

      const nextChildrenState = {
        dict: { ...dict, ...nextDict },
        order: union(order, nextDesiredOrder).sort((a) => (a === animatedKey ? 1 : -1)),
      }
      return nextChildrenState
    })
  }, [childrenProp])

  useEffect(() => {
    if (!shouldAnimate) return
    const animatedKeyExiting = nextChildrenOrder.indexOf(animatedKey) === -1
    animatedState.setValue(animatedKeyExiting ? 1 : 0)
    Animated.spring(animatedState, {
      toValue: animatedKeyExiting ? 2 : 1,
      damping: 18,
      useNativeDriver: true,
    }).start(() => {
      setChildrenState((prevState) => ({ ...prevState, order: nextChildrenOrder }))
    })
  }, [shouldAnimate])

  const animatedStyles = {
    transform: [
      {
        translateY: animatedState.interpolate({
          inputRange: [0, 1, 2],
          outputRange: [containerHeight, 0, -containerHeight],
        }),
      },
    ],
  }

  return (
    <Container
      style={style}
      onLayout={onContainerLayout}
      pointerEvents={shouldAnimate ? 'none' : 'auto'}
    >
      {childrenState.order.map((key) => (
        <AnimatedContainer
          key={key}
          style={[
            style,
            shouldAnimate && StyleSheet.absoluteFill,
            shouldAnimate && { height: containerHeight },
            key === animatedKey && animatedStyles,
          ]}
        >
          {childrenState.dict[key]}
        </AnimatedContainer>
      ))}
    </Container>
  )
}

export default IntakeTransition
